
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface QuotationButtonProps {
  onViewQuotation: () => Promise<void>;
  isGenerating: boolean;
}

const QuotationButton = ({ onViewQuotation, isGenerating }: QuotationButtonProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  
  // Simplified handler with clear logging and scope isolation
  const handleViewQuotation = async (e: React.MouseEvent) => {
    console.log("QuotationButton: Click registered", { timestamp: Date.now() });
    
    // Prevent default and explicitly STOP propagation to avoid interference with submit button
    e.preventDefault();
    e.stopPropagation();
    
    if (isGenerating) {
      console.log("QuotationButton: Already generating, ignoring click");
      return;
    }
    
    console.log("QuotationButton: Calling onViewQuotation");
    
    try {
      await onViewQuotation();
      console.log("QuotationButton: Quotation generation completed successfully");
    } catch (error) {
      console.error("QuotationButton: Error generating quotation:", error);
      toast({
        title: "Error",
        description: "Failed to generate quotation. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  return (
    <Button 
      variant="outline"
      onClick={handleViewQuotation}
      disabled={isGenerating}
      className="relative overflow-hidden bg-gradient-to-r from-card via-accent/40 to-card border border-border/60 hover:border-primary/40 shadow hover:shadow-lg transition-all duration-300 gap-3 px-4 sm:px-6 py-5 sm:py-6 rounded-lg z-10 group"
      type="button"
      data-testid="view-quotation-button"
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
    >
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent group-hover:animate-[shine_2s_ease_infinite] opacity-0 group-hover:opacity-100"></div>
        <div className="absolute -inset-x-10 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent group-hover:animate-[shine_2s_ease_infinite_200ms] opacity-0 group-hover:opacity-100"></div>
      </div>
      
      <div className="relative z-10 h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-all duration-300 overflow-hidden">
        <FileText 
          size={18} 
          className="text-primary transform group-hover:scale-110 transition-transform duration-300"
        />
      </div>
      
      <span className="font-medium tracking-wide relative">
        {isGenerating ? (
          <span className="flex items-center gap-3">
            <span className="h-4 w-4 rounded-full border-2 border-primary/70 border-t-transparent animate-spin"></span>
            <span className="text-primary/90">{t("processing")}</span>
          </span>
        ) : (
          <span className="group-hover:text-primary/90 transition-colors duration-300 flex items-center">
            {t("viewInvoice")}
            <span className="inline-block ml-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">→</span>
          </span>
        )}
      </span>
    </Button>
  );
};

export default QuotationButton;
